"use client";

import Link from "next/link";
import { forwardRef, useState } from "react";
import type { ComponentProps } from "react";

import styles from "./prefetch-link.module.css";

type PrefetchLinkProps = Omit<ComponentProps<typeof Link>, "prefetch">;

/**
 * Intent-prefetched `<Link>` — the official Next.js `HoverPrefetchLink`
 * pattern (see nextjs.org/docs …/guides/prefetching), extended to touch +
 * keyboard, and `forwardRef` so it drops into `<Button render={…}>` /
 * `<SheetClose render={…}>` (the render target gets props + ref merged onto it).
 *
 * Storefront nav targets are all ISR-cached, so a `prefetch={null}` (auto)
 * prefetch pulls their FULL static payload — a subsequent click paints
 * instantly from the router cache (`staleTimes` 120/180s). But eager viewport
 * prefetch of every header/footer link at load pulled ~20 route chunks (incl.
 * the Clerk SDK) into the critical path, competing with the LCP image on
 * throttled mobile. So we start at `prefetch={false}` (no viewport, no load
 * cost) and flip to `prefetch={null}` on the FIRST sign of intent:
 *   - `onPointerEnter` — desktop hover AND the first touch-point contact
 *     (fires just before `pointerdown`, so a mobile tap warms the route
 *     before the click resolves);
 *   - `onFocus` — keyboard tab-to-link.
 *
 * Once flipped, Next prefetches once and caches it; further intent is a no-op.
 * This keeps the LCP protected (nothing prefetches at load) while making every
 * deliberate navigation instant. Any `onPointerEnter`/`onFocus` a caller
 * passes (e.g. via `asChild`) is preserved and called after activation.
 */
export const PrefetchLink = forwardRef<HTMLAnchorElement, PrefetchLinkProps>(
  function PrefetchLink({ children, onPointerEnter, onFocus, ...props }, ref) {
    const [active, setActive] = useState(false);
    return (
      <Link
        className={styles.link}
        {...props}
        ref={ref}
        prefetch={active ? null : false}
        onPointerEnter={(e) => {
          setActive(true);
          onPointerEnter?.(e);
        }}
        onFocus={(e) => {
          setActive(true);
          onFocus?.(e);
        }}
      >
        {children}
      </Link>
    );
  },
);
